import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as DropdownDownIcon } from "./assets/dropdown-iterative-down.svg";
import { ReactComponent as DropdownUpIcon } from "./assets/dropdown-iterative-up.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as MinusIcon } from "./assets/minus.svg";
import {
  CounterButton,
  CounterContainer,
  CounterInputContainer,
  CounterInputContent,
  CounterInputField,
  CounterInputSection,
  CounterOverlay,
  CounterValue,
  CounterValueContainer,
  CyclesSection,
  DropdownItem,
  DropdownList,
  MainContainer,
  MinusIconContainer,
  PlusIconContainer,
  SecondaryLabel,
  SectionContent,
  SectionHeader,
  SectionTitle,
  VehicleDropdownIcon,
  VehicleInputContainer,
  VehicleInputContent,
  VehicleInputField,
  VehicleInputSection,
  VehiclePlaceholder,
  VehicleSection,
} from "./styles";
import {
  VEHICLE_DROPDOWN_SELECTOR,
  MIN_CYCLES,
  isClickOutsideElement,
  calculateNewCycles,
  getDisplayVehicleName,
} from "./utils";

interface MissionDeployProps {
  handleVehiclePick: (vehicleId: string, selectedLoopsValue: string) => void;
  handleFleetPick: (currentFleetId: string, selectedLoopsValue: string) => void;
  cancelAction: () => void;
  currentMissionDataStops: Array<string>;
  vehicleName: string;
  vehicleId: string | null;
  currentVehicles: Vehicle[];
  cycles?: number;
  onCyclesChange?: (cycles: number) => void;
  resetTrigger?: number; // Add this to trigger reset
}

export const MissionDeploy: React.FC<MissionDeployProps> = ({
  handleVehiclePick,
  currentMissionDataStops,
  vehicleName,
  currentVehicles,
  cycles = 1,
  onCyclesChange,
  resetTrigger = 0,
}) => {
  const [localCycles, setLocalCycles] = useState(cycles);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Computed values
  const isOnlyOneStop = currentMissionDataStops.length === 1;
  const displayVehicleName = getDisplayVehicleName(
    selectedVehicle,
    vehicleName
  );

  // Effects
  useEffect(() => {
    setSelectedVehicle(null);
  }, [resetTrigger]);

  useEffect(() => {
    if (isOnlyOneStop && localCycles !== MIN_CYCLES) {
      setLocalCycles(MIN_CYCLES);
      onCyclesChange?.(MIN_CYCLES);
      if (selectedVehicle) {
        handleVehiclePick(selectedVehicle.id, MIN_CYCLES.toString());
      }
    }
  }, [
    isOnlyOneStop,
    localCycles,
    onCyclesChange,
    selectedVehicle,
    handleVehiclePick,
  ]);

  // Event handlers
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (isDropdownOpen) {
        const target = event.target as Element;
        if (isClickOutsideElement(target, VEHICLE_DROPDOWN_SELECTOR)) {
          setIsDropdownOpen(false);
        }
      }
    },
    [isDropdownOpen]
  );

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [isDropdownOpen, handleClickOutside]);

  const updateCyclesAndNotifyParent = useCallback(
    (newCycles: number) => {
      setLocalCycles(newCycles);
      onCyclesChange?.(newCycles);
      if (selectedVehicle) {
        handleVehiclePick(selectedVehicle.id, newCycles.toString());
      }
    },
    [selectedVehicle, onCyclesChange, handleVehiclePick]
  );

  const handleDecreaseCycles = useCallback(() => {
    const newCycles = calculateNewCycles(localCycles, "decrease");
    updateCyclesAndNotifyParent(newCycles);
  }, [localCycles, updateCyclesAndNotifyParent]);

  const handleIncreaseCycles = useCallback(() => {
    const newCycles = calculateNewCycles(localCycles, "increase");
    updateCyclesAndNotifyParent(newCycles);
  }, [localCycles, updateCyclesAndNotifyParent]);

  const handleDropdownToggle = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const handleVehicleSelect = useCallback(
    (vehicle: Vehicle) => {
      setSelectedVehicle(vehicle);
      setIsDropdownOpen(false);
      handleVehiclePick(vehicle.id, localCycles.toString());
    },
    [handleVehiclePick, localCycles]
  );

  return (
    <MainContainer>
      <CyclesSection>
        <SectionHeader>
          <SectionTitle>Number of Cycles</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <CounterContainer>
            <CounterButton onClick={handleDecreaseCycles}>
              <MinusIconContainer>
                <MinusIcon />
              </MinusIconContainer>
            </CounterButton>
            <CounterInputContainer>
              <CounterInputSection>
                <CounterInputField>
                  <CounterOverlay />
                  <CounterInputContent>
                    <CounterValueContainer>
                      <CounterValue>{localCycles}</CounterValue>
                    </CounterValueContainer>
                  </CounterInputContent>
                </CounterInputField>
              </CounterInputSection>
            </CounterInputContainer>
            <CounterButton onClick={handleIncreaseCycles}>
              <PlusIconContainer>
                <AddIcon />
              </PlusIconContainer>
            </CounterButton>
          </CounterContainer>
        </SectionContent>
      </CyclesSection>
      <VehicleSection>
        <SectionHeader>
          <SectionTitle>Assign to vehicle</SectionTitle>
        </SectionHeader>
        <VehicleInputContainer>
          <VehicleInputSection data-vehicle-dropdown>
            <VehicleInputField onClick={handleDropdownToggle}>
              <VehicleInputContent>
                <VehiclePlaceholder>
                  <SecondaryLabel>{displayVehicleName}</SecondaryLabel>
                </VehiclePlaceholder>
              </VehicleInputContent>
              <VehicleDropdownIcon>
                {isDropdownOpen ? <DropdownUpIcon /> : <DropdownDownIcon />}
              </VehicleDropdownIcon>
            </VehicleInputField>
            {isDropdownOpen && (
              <DropdownList>
                {currentVehicles.length > 0 ? (
                  currentVehicles.map((vehicle, index) => (
                    <DropdownItem
                      key={vehicle.id || index}
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      {vehicle.name}
                    </DropdownItem>
                  ))
                ) : (
                  <DropdownItem>No vehicles available</DropdownItem>
                )}
              </DropdownList>
            )}
          </VehicleInputSection>
        </VehicleInputContainer>
      </VehicleSection>
    </MainContainer>
  );
};
